import _ from "lodash";
import React, { useState } from "react";
import moment from "moment";
import { useI18n, useMount } from "Hooks";

import classNames from "classnames";
import classes from "./DateSelectorModal.scss";
import {
  Avatar,
  Button,
  DatePicker,
  Modal,
  Table,
  Tooltip,
} from "@toddle-edu/ds-web";
import { StudentOutlined } from "@toddle-edu/ds-icons";

import {
  getTableHeadersMemoized,
  getAvatarData,
} from "./DateSelectorModalUtils";

const MODAL_DIMENSIONS = {
  width: 800,
  height: 424,
};

//* Function to get the class avatar
const getClassAvatar = ({ courseClassList, id }) => {
  const associatedClass = _.find(courseClassList, { id });
  const { color, name, AvatarIcon, iconType } = getAvatarData(associatedClass);

  return (
    <Avatar
      icon={<AvatarIcon size="xx-small" />}
      color={color}
      name={name}
      iconType={iconType}
      shape="square"
      theme="light"
    />
  );
};

//* Function to format the class list
const getFormattedClassList = ({ startDate, endDate, classDates }) => {
  const formattedClassList = _.map(classDates, list => {
    return {
      classId: list.classId,
      startDate: list?.startDate || startDate,
      endDate: list?.endDate || endDate,
    };
  });
  return formattedClassList;
};

const DateSelectorModal = ({
  onClose,
  startDate,
  endDate,
  minDate,
  maxDate,
  onDateChange,
  classDates,
  courseClassList,
}) => {
  const { t } = useI18n(["common", "unitPlan"]);
  const modalTitle = t("unitPlan:date_selector_modal_title");
  const [classList, setClassList] = useState([]);

  //* Function to handle date change
  const handleChangeDate = params => {
    const newClassList = classList.map(item =>
      item.classId === params.classId
        ? {
            classId: params.classId,
            startDate: params.startDate,
            endDate: params.endDate,
          }
        : item
    );

    setClassList(newClassList);

    const updatedClassList = { classDates: [...newClassList] };
    onDateChange({ ...params }, updatedClassList);
  };

  //* Function to get the table headers
  const getTableBodyData = ({
    classList,
    tableHeaders,
    minDate,
    maxDate,
    courseClassList,
  }) => {
    return _.map(classList, list => {
      const { classId: id, startDate, endDate } = list;
      const title = _.get(_.find(courseClassList, { id }), "title", "");
      const rowData = _.map(tableHeaders, header => {
        const { key } = header;

        let value, style;
        switch (key) {
          case "class": {
            value = (
              <div className={classes.classLabel}>
                {getClassAvatar({ courseClassList, id })}
                <Tooltip
                  tooltip={title}
                  placement={"top"}
                  showArrow={true}
                  visible={_.size(title) > 15}
                >
                  <span
                    className={classNames(
                      "text-body",
                      classes.classTitleLabel,
                      "truncate"
                    )}
                  >
                    {title}
                  </span>
                </Tooltip>
              </div>
            );
            break;
          }
          case "startDate":
            {
              style = {
                padding: "0px",
              };
              value = (
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    format="DD MMM YYYY"
                    value={moment(startDate)}
                    minDate={moment(minDate)}
                    maxDate={moment(maxDate)}
                    variant="inline"
                    onChange={date =>
                      handleChangeDate({
                        [key]: date.format("YYYY-MM-DD"),
                        endDate: endDate,
                        classId: id,
                      })
                    }
                  />
                </div>
              );
            }
            break;
          case "endDate":
            {
              style = {
                padding: "0px",
              };
              value = (
                <div className={classes.datePickerWrapper}>
                  <DatePicker
                    format="DD MMM YYYY"
                    variant="inline"
                    value={moment(endDate)}
                    minDate={moment(startDate)}
                    maxDate={moment(maxDate)}
                    startDate={moment(startDate)}
                    onChange={date =>
                      handleChangeDate({
                        startDate: startDate,
                        [key]: date.format("YYYY-MM-DD"),
                        classId: id,
                      })
                    }
                  />
                </div>
              );
            }
            break;
        }

        return { key, value, style };
      });

      return { id, rowData };
    });
  };

  //* Function to get the table body data
  const getModalBody = ({ classList, minDate, maxDate }) => {
    const tableHeaders = getTableHeadersMemoized();
    const tableBodyData = getTableBodyData({
      classList,
      tableHeaders,
      minDate,
      maxDate,
      courseClassList,
    });

    return (
      <Table
        dsVersion="2.0"
        headers={tableHeaders}
        headerTheme="subtle"
        data={tableBodyData}
        showBorder={true}
        isHeaderFixed={false}
        showRowBackgroundOnHover={false}
      />
    );
  };

  const modalBody = getModalBody({
    classList,
    startDate,
    endDate,
    minDate,
    maxDate,
  });

  useMount(() => {
    const formattedClassList = getFormattedClassList({
      courseClassList,
      startDate,
      endDate,
      classDates,
    });
    setClassList(formattedClassList);
  });

  return (
    <Modal {...MODAL_DIMENSIONS} onClose={onClose}>
      <Modal.Header isClosable>
        <h6 className={classNames(classes.modalTitle)}>{modalTitle}</h6>
      </Modal.Header>
      <Modal.Body isScrollable={true}>
        <div className={classes.modalBody}>
          <div className={classes.classLabel}>
            <StudentOutlined size="xx-small" variant="subtle" />
            <span className="text-label-l">{t("common:class_plural")}</span>
          </div>
          {modalBody}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.modalFooter}>
          <Button
            size="medium"
            variant="neutral"
            type="outlined"
            onClick={onClose}
          >
            {t("common:close")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DateSelectorModal;
